import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import SubscriptionBanner from "../../components/SubscriptionBanner";
import { Coins, TrendingUp, TrendingDown, Gift, BookOpen, AlertTriangle, CreditCard } from "lucide-react";

const TYPE_LABEL = {
    signup_bonus: { icon: <Gift className="h-4 w-4" />, label: "Signup bonus", tone: "library" },
    book_post_bonus: { icon: <BookOpen className="h-4 w-4" />, label: "Book posted", tone: "library" },
    borrow_debit: { icon: <TrendingDown className="h-4 w-4" />, label: "Borrowed a book", tone: "red" },
    lend_credit: { icon: <TrendingUp className="h-4 w-4" />, label: "Lent a book", tone: "zapp" },
    subscription_payment: { icon: <CreditCard className="h-4 w-4" />, label: "Membership payment", tone: "library" },
};

export default function Wallet() {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/wallet").then(({ data }) => setData(data));
    }, []);

    const coins = data?.zapp_coins ?? user?.zapp_coins ?? 0;
    const negative = coins < 0;

    return (
        <div className="max-w-4xl mx-auto px-6 py-10">
            <div className="mb-8">
                <div className="label-mini">Wallet</div>
                <h1 className="font-serif text-4xl font-semibold">Your Zapp Coins</h1>
                <p className="text-ink/60 mt-2">Every book you post earns 100 coins. Coins settle when a book is returned.</p>
            </div>

            <div className={`rounded-2xl p-8 mb-8 border ${negative ? "bg-red-50 border-red-200" : "bg-ink text-paper border-ink"}`} data-testid="wallet-balance-card">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold opacity-70 mb-2">
                    <Coins className="h-4 w-4" /> Balance
                </div>
                <div className={`font-serif text-6xl font-semibold ${negative ? "text-red-800" : ""}`} data-testid="wallet-balance-value">
                    {coins} <span className="text-2xl opacity-70">Zapp</span>
                </div>
                {negative && (
                    <div className="mt-4 flex items-start gap-2 text-red-800 text-sm">
                        <AlertTriangle className="h-4 w-4 mt-0.5" />
                        <p>Your balance is negative. Post more books to earn +100 coins each and get back into the green.</p>
                    </div>
                )}
            </div>

            <div className="bg-white border border-parchment rounded-2xl p-6 shadow-sm">
                <h2 className="font-serif text-2xl font-semibold mb-4">Recent activity</h2>
                {!data ? (
                    <div className="text-ink/50">Loading…</div>
                ) : data.transactions.length === 0 ? (
                    <div className="text-sm text-ink/60">No transactions yet.</div>
                ) : (
                    <ul className="divide-y divide-parchment">
                        {data.transactions.map((t) => {
                            const meta = TYPE_LABEL[t.type] || { icon: <Coins className="h-4 w-4" />, label: t.type, tone: "ink" };
                            const positive = t.amount >= 0;
                            return (
                                <li key={t.id} className="py-3 flex items-center justify-between gap-3" data-testid={`wallet-txn-${t.id}`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`h-9 w-9 rounded-full flex items-center justify-center ${
                                            meta.tone === "library" ? "bg-library/10 text-library" :
                                            meta.tone === "zapp" ? "bg-zapp/10 text-zapp-dark" :
                                            meta.tone === "red" ? "bg-red-100 text-red-700" :
                                            "bg-paper text-ink"
                                        }`}>{meta.icon}</div>
                                        <div>
                                            <div className="font-medium text-sm">{meta.label}</div>
                                            <div className="text-xs text-ink/50">{t.note} · {new Date(t.created_at).toLocaleString()}</div>
                                        </div>
                                    </div>
                                    <div className={`font-mono font-bold ${positive ? "text-library" : "text-red-700"}`}>
                                        {positive ? "+" : ""}{t.amount}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}
